import { z } from 'zod';
import { DASHBOARD_PAGES, REPORT_VISIBILITIES } from '@bulk/types';
import { idSchema, patchOf } from './common';
import { REPORT_DATASET_RULES, REPORT_MEASURES, isTimeGrouping, reportDefinitionSchema, reportMeasureAdds, type ReportDefinition, type ReportGrouping, type ReportMeasure, type ReportProblem } from './reports';

export const DASHBOARD_CHART_TYPES = ['column', 'bar', 'line', 'area', 'stacked_column', 'grouped_column', 'donut', 'pie', 'number', 'table'] as const;
export type DashboardChartType = (typeof DASHBOARD_CHART_TYPES)[number];

/** A widget is a whole number of columns wide on a 12-column grid, and a whole number of rows tall. */
export const DASHBOARD_COLUMNS = 12;
export const DASHBOARD_MIN_WIDTH = 3;
export const DASHBOARD_MIN_ROWS = 3;
export const DASHBOARD_MAX_ROWS = 12;

/** Boards saved before rows (OBO-536) stored a size name. */
const LEGACY_ROWS = { S: 4, M: 6, L: 8 } as const;

const widthSchema = z.number().int().min(DASHBOARD_MIN_WIDTH).max(DASHBOARD_COLUMNS);
const heightSchema = z.union([z.number().int().min(DASHBOARD_MIN_ROWS).max(DASHBOARD_MAX_ROWS), z.enum(['S', 'M', 'L']).transform((size) => LEGACY_ROWS[size])]);

/** Near-white shades for a widget's title band, so neighbours are told apart; each has a dark step in `index.css`. */
export const DASHBOARD_TINTS = ['white', 'blue', 'teal', 'green', 'yellow', 'orange', 'pink', 'purple'] as const;
export type DashboardTint = (typeof DASHBOARD_TINTS)[number];
export const DASHBOARD_SHADES = DASHBOARD_TINTS.filter((tint) => tint !== 'white');

/** A widget saved before tints takes a shade picked by its id, so an old board is not all white and never changes shade between loads. */
export function tintForId(id: string): DashboardTint {
  const hash = [...id].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) >>> 0, 7);
  return DASHBOARD_SHADES[hash % DASHBOARD_SHADES.length] ?? 'white';
}

export const DASHBOARD_MAX_WIDGETS = 24;

interface ChartRule {
  /** How many groupings the chart plots: the second one becomes its series. */
  groupings: 1 | 2 | 'any';
  /** The first grouping must run in order: a period or the weekday. */
  ordered?: boolean;
  /** The first grouping must be a category, never a period. */
  categorical?: boolean;
  /** Averages and rates cannot be added up, so part-to-whole charts refuse them. */
  sums?: boolean;
}

export const DASHBOARD_CHART_RULES: Record<DashboardChartType, ChartRule> = {
  column: { groupings: 1 },
  bar: { groupings: 'any' },
  line: { groupings: 'any', ordered: true },
  area: { groupings: 'any', ordered: true },
  stacked_column: { groupings: 2, sums: true },
  grouped_column: { groupings: 2 },
  donut: { groupings: 1, categorical: true, sums: true },
  pie: { groupings: 1, categorical: true, sums: true },
  number: { groupings: 'any' },
  table: { groupings: 'any' },
};

const isOrdered = (grouping: ReportGrouping | undefined) => grouping !== undefined && (isTimeGrouping(grouping) || grouping === 'weekday');

const widgetFields = {
  id: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/),
  title: z.string().trim().max(120).default(''),
  chart: z.enum(DASHBOARD_CHART_TYPES),
  measure: z.enum(REPORT_MEASURES),
  width: widthSchema,
  height: heightSchema,
  report: reportDefinitionSchema,
};

const dashboardWidgetShape = z.object({ ...widgetFields, tint: z.enum(DASHBOARD_TINTS).optional() }).transform(({ tint, ...widget }) => ({ ...widget, tint: tint ?? tintForId(widget.id) }));
export type DashboardWidget = z.output<typeof dashboardWidgetShape>;

export type WidgetProblemCode = 'measure' | 'twoGroupings' | 'oneGrouping' | 'ordered' | 'categorical' | 'sums';

export interface WidgetProblem extends ReportProblem {
  code: WidgetProblemCode;
}

export function widgetProblems(widget: Pick<DashboardWidget, 'chart' | 'measure' | 'report'>): WidgetProblem[] {
  const problems: WidgetProblem[] = [];
  const rule = DASHBOARD_CHART_RULES[widget.chart];
  const { groupBy, measures } = widget.report;
  const [first] = groupBy;
  if (!measures.includes(widget.measure)) problems.push({ code: 'measure', path: ['measure'], message: `The chart plots ${widget.measure}, which the data does not show; add it to the measures` });
  if (rule.groupings === 2 && groupBy.length !== 2) problems.push({ code: 'twoGroupings', path: ['report', 'groupBy'], message: 'This chart needs a second grouping for its series' });
  if (rule.groupings === 1 && groupBy.length !== 1) problems.push({ code: 'oneGrouping', path: ['report', 'groupBy'], message: 'This chart plots one grouping; remove the second one' });
  if (rule.ordered && !isOrdered(first)) problems.push({ code: 'ordered', path: ['report', 'groupBy', 0], message: 'A line or area runs along a period or the weekday; group by day, week, month, quarter or weekday first' });
  if (rule.categorical && (first === undefined || isTimeGrouping(first))) problems.push({ code: 'categorical', path: ['report', 'groupBy', 0], message: 'A donut or pie shows parts of a whole; group by a category, not a period' });
  if (rule.sums && !reportMeasureAdds(widget.measure)) problems.push({ code: 'sums', path: ['measure'], message: 'Averages and rates do not add up to a whole; plot the amount or the count' });
  return problems;
}

export const dashboardWidgetSchema = dashboardWidgetShape.superRefine((widget, ctx) => {
  for (const problem of widgetProblems(widget)) ctx.addIssue({ code: 'custom', path: problem.path, message: problem.message });
});

function firstOther(groupings: readonly ReportGrouping[], avoid: readonly (ReportGrouping | undefined)[], accept: (grouping: ReportGrouping) => boolean): ReportGrouping | undefined {
  return groupings.find((grouping) => !avoid.includes(grouping) && accept(grouping));
}

/** The nearest widget that the chart can draw: groupings and the plotted measure moved only as far as the chart needs. */
export function fitWidgetToChart<W extends Pick<DashboardWidget, 'chart' | 'measure' | 'report'>>(widget: W, chart: DashboardChartType): W {
  const rule = DASHBOARD_CHART_RULES[chart];
  const allowed = REPORT_DATASET_RULES[widget.report.dataset].groupings;
  let [first, second] = widget.report.groupBy;
  if (rule.ordered && !isOrdered(first)) first = firstOther(allowed, [], (g) => g === 'month') ?? firstOther(allowed, [], isOrdered) ?? first;
  if (rule.categorical && (first === undefined || isTimeGrouping(first))) first = firstOther(allowed, [], (g) => !isTimeGrouping(g)) ?? first;
  if (rule.groupings === 1) second = undefined;
  if (rule.groupings === 2 && !second) second = firstOther(allowed, [first], (g) => !isTimeGrouping(g) && g !== 'member' && !(first !== undefined && isTimeGrouping(first) && isTimeGrouping(g)));
  if (second === first || (first && second && isTimeGrouping(first) && isTimeGrouping(second))) second = undefined;
  const groupBy = (second ? [first, second] : [first]).filter((g): g is ReportGrouping => g !== undefined);
  let measure: ReportMeasure = widget.measure;
  if (rule.sums && !reportMeasureAdds(measure)) measure = widget.report.measures.includes('amount') ? 'amount' : 'count';
  const measures = widget.report.measures.includes(measure) ? widget.report.measures : REPORT_MEASURES.filter((m) => m === measure || widget.report.measures.includes(m));
  const report: ReportDefinition = { ...widget.report, groupBy: groupBy.length > 0 ? groupBy : widget.report.groupBy, measures };
  return { ...widget, chart, measure, report };
}

const widgetsSchema = z
  .array(dashboardWidgetSchema)
  .max(DASHBOARD_MAX_WIDGETS)
  .superRefine((widgets, ctx) => {
    const seen = new Set<string>();
    widgets.forEach((widget, index) => {
      if (seen.has(widget.id)) ctx.addIssue({ code: 'custom', path: [index, 'id'], message: 'Each chart on a board needs its own id' });
      seen.add(widget.id);
    });
  });

export const dashboardInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  page: z.enum(DASHBOARD_PAGES),
  visibility: z.enum(REPORT_VISIBILITIES).default('PRIVATE'),
  widgets: widgetsSchema.default([]),
});
export type DashboardInput = z.infer<typeof dashboardInputSchema>;

export const updateDashboardSchema = patchOf(dashboardInputSchema);
export type UpdateDashboardInput = z.infer<typeof updateDashboardSchema>;

export const dashboardListQuerySchema = z.object({ page: z.enum(DASHBOARD_PAGES) });

export const dashboardSchema = z.object({
  id: idSchema,
  name: z.string(),
  page: z.enum(DASHBOARD_PAGES),
  visibility: z.enum(REPORT_VISIBILITIES),
  widgets: z.array(z.object({ ...widgetFields, tint: z.enum(DASHBOARD_TINTS) })),
  problems: z.array(z.string()),
  createdByUserId: idSchema.nullable(),
  createdByName: z.string().nullable(),
  canEdit: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DashboardDto = z.infer<typeof dashboardSchema>;

export const DASHBOARD_EXPORT_FORMATS = ['pdf', 'pptx'] as const;
export type DashboardExportFormat = (typeof DASHBOARD_EXPORT_FORMATS)[number];

export const DASHBOARD_EXPORT_LIMITS = { categories: 400, series: 8, text: 200, tableRows: 200, tableColumns: 20 } as const;

const exportText = (max: number = DASHBOARD_EXPORT_LIMITS.text) => z.string().max(max);
const exportValues = z.array(z.number().finite()).max(DASHBOARD_EXPORT_LIMITS.categories);

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

const exportSeriesSchema = z.object({
  name: exportText(),
  color: hexColor,
  values: exportValues,
  labels: z.array(exportText(60)).max(DASHBOARD_EXPORT_LIMITS.categories),
});

/** One chart as the viewer saw it: every word and every formatted number comes from the browser, which owns the translations. */
export const dashboardExportWidgetSchema = z
  .object({
    title: exportText(),
    subtitle: exportText(),
    chart: z.enum(DASHBOARD_CHART_TYPES),
    width: widthSchema,
    height: heightSchema,
    /** The title band's shade, light as the files always are. */
    tint: hexColor.optional(),
    message: exportText().nullable(),
    categories: z.array(exportText()).max(DASHBOARD_EXPORT_LIMITS.categories),
    series: z.array(exportSeriesSchema).max(DASHBOARD_EXPORT_LIMITS.series),
    totals: z.array(exportText(60)).max(DASHBOARD_EXPORT_LIMITS.categories),
    grandTotal: exportText(60).nullable(),
    shares: z.array(exportText(20)).max(DASHBOARD_EXPORT_LIMITS.categories),
    /** A donut's or pie's slice colours, one per category. */
    sliceColors: z.array(hexColor).max(DASHBOARD_EXPORT_LIMITS.categories),
    previous: z.object({ label: exportText(), values: exportValues }).nullable(),
    ticks: z.array(z.object({ value: z.number().finite(), label: exportText(60) })).max(10),
    valueFormat: exportText(60),
    number: z.object({ value: exportText(60), change: exportText(60).nullable(), tone: z.enum(['success', 'danger', 'muted']).nullable(), previous: exportText(120).nullable(), period: exportText(120) }).nullable(),
    table: z
      .object({
        headers: z.array(exportText()).max(DASHBOARD_EXPORT_LIMITS.tableColumns),
        rows: z.array(z.array(exportText()).max(DASHBOARD_EXPORT_LIMITS.tableColumns)).max(DASHBOARD_EXPORT_LIMITS.tableRows),
      })
      .nullable(),
  })
  .superRefine((widget, ctx) => {
    const count = widget.categories.length;
    widget.series.forEach((series, i) => {
      if (series.values.length !== count || series.labels.length !== count) ctx.addIssue({ code: 'custom', path: ['series', i], message: 'Each series needs one value and one label per category' });
    });
    if (widget.previous && widget.previous.values.length !== count) ctx.addIssue({ code: 'custom', path: ['previous'], message: 'The comparison needs one value per category' });
  });
export type DashboardExportWidget = z.infer<typeof dashboardExportWidgetSchema>;

export const dashboardExportSchema = z.object({
  format: z.enum(DASHBOARD_EXPORT_FORMATS),
  title: exportText(),
  subtitle: exportText(300),
  /** Words the files print around the charts, in the viewer's language: "Page {page} of {count}", "Total". */
  words: z.object({ pageOf: exportText(60), total: exportText(40) }),
  widgets: z.array(dashboardExportWidgetSchema).min(1).max(DASHBOARD_MAX_WIDGETS),
});
export type DashboardExportInput = z.infer<typeof dashboardExportSchema>;
